import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Instagram } from 'lucide-react';
import { trackWhatsAppClick } from '../lib/api';

const Footer = () => {
  const { pathname } = useLocation();
  // The marketing "start your coffee journey" CTA is for prospects; hide it inside
  // the members-only community (The Brew Circle), where it's out of place.
  const showCta = !pathname.startsWith('/community');

  return (
    <footer style={{ background: "var(--bg-primary)", borderTop: "1px solid var(--border-light)" }}>
      {/* CTA Section */}
      {showCta && (
      <div style={{ borderBottom: "1px solid var(--border-light)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl mb-4" style={{ color: "var(--accent)", fontFamily: "var(--font-heading)" }}>
              READY TO START YOUR COFFEE JOURNEY?
            </h2>
            <p className="text-lg mb-8 opacity-80" style={{ color: "var(--text-primary)" }}>
              Join hundreds of successful baristas and cafe owners who have transformed their careers with us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/courses"
                data-cursor-hover
                className="px-8 py-4 rounded-lg transition-all text-base font-semibold hover:opacity-90"
                style={{ background: "var(--accent)", color: "var(--text-on-accent)" }}
              >
                View Courses
              </Link>
              <a
                href="https://wa.me/919687999960"
                target="_blank"
                rel="noopener noreferrer"
                data-cursor-hover
                onClick={() => trackWhatsAppClick('footer')}
                className="px-8 py-4 rounded-lg transition-all text-base font-semibold hover:opacity-80"
                style={{ border: "2px solid var(--border-accent)", color: "var(--accent)" }}
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto py-12 sm:py-14 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">

          {/* Brand */}
          <div>
            <div className="mb-5 sm:mb-6">
              <h3 className="text-xl sm:text-2xl tracking-[0.2em] font-bold leading-tight" style={{ color: "var(--text-primary)", fontFamily: "var(--font-heading-alt)" }}>
                BREWING<br /><span style={{ color: "var(--accent)" }}>SKILL CO.</span>
              </h3>
              <p className="text-xs sm:text-sm tracking-[0.12em] mt-2" style={{ color: "var(--accent-deep)", fontFamily: "var(--font-decorative)" }}>
                — CAFE ACADEMY —
              </p>
            </div>
            <p className="text-xs sm:text-sm leading-relaxed mb-5 sm:mb-6" style={{ color: "var(--text-secondary)" }}>
              Gujarat's leading specialty coffee education institution. Offering world-class barista training, coffee education, and consultancy since 2019.
            </p>
            <a href="https://www.instagram.com/brewingskill.co/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-5 text-xs sm:text-sm transition-colors hover:opacity-80" style={{ color: "var(--accent-text-strong)" }}>
              <Instagram size={16} style={{ color: "var(--accent)" }} />
              @brewingskill.co
            </a>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-[12px] sm:text-[13px] tracking-[0.3em] font-normal mb-5 sm:mb-6" style={{ color: "var(--accent)", fontFamily: "var(--font-heading-alt)" }}>
              NAVIGATE
            </h4>
            <div className="flex flex-col gap-3 sm:gap-4">
              {[{ to: '/', label: 'Home' }, { to: '/about', label: 'About' }, { to: '/courses', label: 'Courses' }, { to: '/blog', label: 'Blog' }, { to: '/answers', label: 'Answers' }, { to: '/contact', label: 'Contact' }].map(l => (
                <Link key={l.to} to={l.to} data-cursor-hover className="text-xs sm:text-sm transition-all duration-200 hover:pl-1.5" style={{ color: "var(--text-secondary)" }}>
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-[12px] sm:text-[13px] tracking-[0.3em] font-normal mb-5 sm:mb-6" style={{ color: "var(--accent)", fontFamily: "var(--font-heading-alt)" }}>
              PROGRAMS
            </h4>
            <div className="flex flex-col gap-3 sm:gap-4">
              {[
                { to: '/courses/basic-barista', label: 'Basic Barista Training' },
                { to: '/courses/latte-art-crash-course', label: 'Latte Art Crash Course' },
                { to: '/courses/cafe-management', label: 'Cafe Management' },
                { to: '/courses', label: 'All Workshops' },
              ].map((c) => (
                <Link key={c.to} to={c.to} data-cursor-hover className="text-xs sm:text-sm transition-all duration-200 hover:pl-1.5" style={{ color: "var(--text-secondary)" }}>
                  {c.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-[12px] sm:text-[13px] tracking-[0.3em] font-normal mb-5 sm:mb-6" style={{ color: "var(--accent)", fontFamily: "var(--font-heading-alt)" }}>
              LEGAL
            </h4>
            <div className="flex flex-col gap-3 sm:gap-4">
              {[{ to: '/terms', label: 'Terms & Conditions' }, { to: '/privacy', label: 'Privacy Policy' }, { to: '/refund-policy', label: 'Refund Policy' }, { to: '/shipping-policy', label: 'Shipping Policy' }].map(l => (
                <Link key={l.to} to={l.to} data-cursor-hover className="text-xs sm:text-sm transition-all duration-200 hover:pl-1.5" style={{ color: "var(--text-secondary)" }}>
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[12px] sm:text-[13px] tracking-[0.3em] font-normal mb-5 sm:mb-6" style={{ color: "var(--accent)", fontFamily: "var(--font-heading-alt)" }}>
              CONTACT
            </h4>
            <div className="flex flex-col gap-4">
              <div className="flex gap-3 items-start">
                <MapPin size={13} className="flex-shrink-0 mt-0.5" style={{ color: "var(--accent-deep)" }} />
                <div className="flex flex-col gap-2">
                  <div>
                    <span className="text-[10px] tracking-[0.2em] block mb-0.5" style={{ color: "var(--accent-text-strong)", fontFamily: "var(--font-heading-alt)" }}>VADODARA</span>
                    <span className="text-xs sm:text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>Akota Garden Main Road, Sheetal Nagar, Akota, Vadodara, Gujarat 390020</span>
                  </div>
                  <div>
                    <span className="text-[10px] tracking-[0.2em] block mb-0.5" style={{ color: "var(--accent-text-strong)", fontFamily: "var(--font-heading-alt)" }}>AHMEDABAD</span>
                    <span className="text-xs sm:text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>Haash Business Centre, FF105, Vasna Rd, Paldi, Ahmedabad, Gujarat 380007</span>
                  </div>
                </div>
              </div>
              {[
                { Icon: Phone, text: '+91 96879 99960', href: 'tel:+919687999960' },
                { Icon: Mail, text: 'hello@brewingskill.com', href: 'mailto:hello@brewingskill.com' },
                { Icon: Clock, text: 'Mon–Sat: 9:00 AM – 6:00 PM' },
              ].map(({ Icon, text, href }, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <Icon size={13} className="flex-shrink-0 mt-0.5" style={{ color: "var(--accent-deep)" }} />
                  {href ? (
                    <a href={href} className="text-xs sm:text-[13px] leading-relaxed transition-colors" style={{ color: "var(--text-secondary)" }}>
                      {text}
                    </a>
                  ) : (
                    <span className="text-xs sm:text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>{text}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Course locations */}
      <div className="py-6 px-4 sm:px-6 lg:px-8" style={{ borderTop: "1px solid var(--border-subtle)" }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <h4 className="text-[12px] sm:text-[13px] tracking-[0.3em] font-normal flex-shrink-0" style={{ color: "var(--accent)", fontFamily: "var(--font-heading-alt)" }}>
            BARISTA COURSES BY CITY
          </h4>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {[
              { to: '/courses/in/baroda', label: 'Vadodara' },
              { to: '/courses/in/ahmedabad', label: 'Ahmedabad' },
              { to: '/courses/in/surat', label: 'Surat' },
              { to: '/courses/in/rajkot', label: 'Rajkot' },
              { to: '/courses/in/gandhinagar', label: 'Gandhinagar' },
            ].map((l) => (
              <Link key={l.to} to={l.to} data-cursor-hover className="text-xs sm:text-sm transition-all duration-200 hover:pl-1.5" style={{ color: "var(--text-secondary)" }}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="py-5 sm:py-6 px-4 sm:px-6 lg:px-8" style={{ borderTop: "1px solid var(--border-subtle)" }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-[12px] sm:text-[13px]" style={{ color: "var(--text-muted)" }}>
            © {new Date().getFullYear()} Brewing Skill Co. (Cafe Academy). All rights reserved.
            {" · "}
            Protected by{" "}
            <a
              href="https://pinaka.sh"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--accent-text-strong)" }}
            >
              Pinaka.sh
            </a>
            {" · "}
            Designed &amp; built by{" "}
            <a
              href="https://haazir.dev"
              target="_blank"
              rel="noopener noreferrer"
              data-cursor-hover
              style={{ color: "var(--accent-text-strong)" }}
            >
              Haazir.dev
            </a>
          </p>
          <p className="text-[12px] sm:text-[13px] text-center sm:text-right" style={{ color: "var(--text-muted)" }}>
            MSME Registered · Authorised Training Partner
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
